import { useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  Box, 
  IconButton, 
  Drawer, 
  List, 
  ListItem, 
  ListItemText,
  Container,
  useMediaQuery,
  useTheme
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import PaymentIcon from '@mui/icons-material/Payment';

const navItems = [
  { name: 'Home', path: '/' },
  { name: 'About Us', path: '/about' },
  { name: 'Announcements', path: '/announcements' },
  { name: 'Events', path: '/events' },
  { name: 'Local Businesses', path: '/neighbor-businesses' },
  { name: 'Helpful Links', path: '/links' },
  { name: 'Contact Us', path: '/contacts' },
  { name: 'Pay Online', path: '/pay-hoa', isAction: true }
];

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const isActive = (path) => {
    if (path === '/' && location.pathname === '/') {
      return true;
    }
    return path !== '/' && location.pathname.startsWith(path);
  };

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center' }}>
      <Typography variant="h6" sx={{ my: 2 }}>
        Brynleigh Estates
      </Typography>
      <List>
        {navItems.map((item) => {
          const active = isActive(item.path);
          return (
            <ListItem 
              key={item.name} 
              component={RouterLink} 
              to={item.path} 
              disablePadding
              sx={{ 
                bgcolor: item.isAction 
                  ? 'primary.main' 
                  : (active ? 'rgba(46, 125, 50, 0.1)' : 'transparent'),
                borderLeft: active && !item.isAction 
                  ? `4px solid ${theme.palette.primary.main}` 
                  : '4px solid transparent',
                my: item.isAction ? 1 : 0,
                borderRadius: item.isAction ? 1 : 0,
                mx: item.isAction ? 2 : 0,
              }}
            >
              <ListItemText 
                primary={item.isAction ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <PaymentIcon sx={{ mr: 1 }} />
                    {item.name}
                  </Box>
                ) : item.name} 
                sx={{ 
                  textAlign: 'center', 
                  py: 1,
                  color: item.isAction 
                    ? 'white' 
                    : (active ? 'primary.main' : 'inherit'),
                  fontWeight: (active || item.isAction) ? 500 : 400,
                }} 
              />
            </ListItem>
          );
        })}
      </List>
    </Box>
  );

  return (
    <AppBar 
      position="static" 
      color="primary" 
      elevation={0}
      sx={{ borderRadius: 0 }}
    >
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography
              variant="h5"
              noWrap
              component={RouterLink}
              to="/"
              sx={{
                color: 'white',
                textDecoration: 'none',
                display: { xs: 'none', sm: 'block' }
              }}
            >
              Brynleigh Estates
            </Typography>
          </Box>

          {isMobile ? (
            <>
              <Box sx={{ flexGrow: 1 }} />
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
              >
                <MenuIcon />
              </IconButton>
            </>
          ) : (
            <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
              {navItems.map((item) => {
                const active = isActive(item.path);
                
                if (item.isAction) {
                  return (
                    <Button
                      key={item.name}
                      component={RouterLink}
                      to={item.path}
                      variant="contained"
                      color="primary"
                      startIcon={<PaymentIcon />}
                      sx={{ 
                        ml: 2,
                        fontWeight: 500,
                        boxShadow: 2
                      }}
                    >
                      {item.name}
                    </Button>
                  );
                }
                
                return (
                  <Button
                    key={item.name}
                    component={RouterLink}
                    to={item.path}
                    sx={{ 
                      color: 'white', 
                      mx: 1,
                      position: 'relative',
                      '&::after': active ? {
                        content: '""',
                        position: 'absolute',
                        bottom: 0,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '60%',
                        height: '3px',
                        backgroundColor: 'white',
                        borderRadius: '3px 3px 0 0'
                      } : {}
                    }}
                  >
                    {item.name}
                  </Button>
                );
              })}
            </Box>
          )}
        </Toolbar>
      </Container>

      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 240 },
        }}
      >
        {drawer}
      </Drawer>
    </AppBar>
  );
};

export default Header;